// LB-480: expiry math for the ThoughtSpot embed token, used by the App.js refresh timer.
export type ThoughtSpotEmbedLike =
  | {
      EmbedToken?: string | null;
      Expiration?: string | number | null;
    }
  | null
  | undefined;

// Refresh this long before expiry (must exceed the 30s timer tick).
export const THOUGHTSPOT_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
// Fallback cadence when the token's real expiry can't be determined.
export const THOUGHTSPOT_TOKEN_FALLBACK_TTL_MS = 50 * 60 * 1000;

// Decode a JWT's `exp` claim (seconds) to epoch ms; null if not a decodable JWT.
export const decodeJwtExpiryMs = (token: unknown): number | null => {
  if (typeof token !== 'string') {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) {
      b64 += '=';
    }
    const json =
      typeof atob === 'function'
        ? atob(b64)
        : Buffer.from(b64, 'base64').toString('binary');
    const payload = JSON.parse(json);
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Best-known expiry (epoch ms): prefer the server Expiration field, then the JWT exp.
export const getThoughtSpotTokenExpiryMs = (
  embed: ThoughtSpotEmbedLike
): number | null => {
  if (!embed) {
    return null;
  }
  const { Expiration, EmbedToken } = embed;
  if (Expiration !== undefined && Expiration !== null && Expiration !== '') {
    const ms =
      typeof Expiration === 'number' ? Expiration : new Date(Expiration).getTime();
    if (!Number.isNaN(ms)) {
      return ms;
    }
  }
  return decodeJwtExpiryMs(EmbedToken);
};

// True when the token should be refreshed: within the buffer of a known expiry, already
// expired, or (expiry unknown) older than the fallback TTL since it was issued.
export const shouldRefreshThoughtSpotToken = (
  embed: ThoughtSpotEmbedLike,
  nowMs: number,
  issuedAtMs?: number | null,
  bufferMs: number = THOUGHTSPOT_TOKEN_REFRESH_BUFFER_MS,
  fallbackTtlMs: number = THOUGHTSPOT_TOKEN_FALLBACK_TTL_MS
): boolean => {
  if (!embed) {
    return false;
  }
  const expiry = getThoughtSpotTokenExpiryMs(embed);
  if (expiry !== null) {
    return nowMs >= expiry - bufferMs;
  }
  if (typeof issuedAtMs === 'number') {
    return nowMs >= issuedAtMs + fallbackTtlMs;
  }
  return false;
};
